import {
  ArrowRight,
  Award,
  CalendarDays,
  ClipboardList,
  Clock,
  Eye,
  Grid3x3,
  MapPin,
  Medal,
  Network,
  Trophy,
  Users,
} from 'lucide-react';
import { useState, type ReactNode, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelectedClub } from '@/features/club';
import {
  allowsSuperTiebreak,
  BEST_OF_LABELS,
  BEST_OF_OPTIONS,
  buildCreateCompetitionPayload,
  buildCreateConsolationPayload,
  buildUpdateCompetitionPayload,
  draftFromCompetition,
  eligibleConsolationSources,
  hasAdvancementSettings,
  planClassificationPhase,
  CATEGORY_OPTIONS,
  COMPETITION_CATEGORY_LABELS,
  COMPETITION_SYSTEM_LABELS,
  COMPETITION_TYPE_LABELS,
  COMPETITION_TYPE_OPTIONS,
  DISCIPLINE_CODE_LABELS,
  DISCIPLINE_OPTIONS,
  hasBracketPhase,
  hasGroupPhase,
  hasPromotionRules,
  INITIAL_CONSOLATION_DRAFT,
  INITIAL_DRAFT,
  MATCH_TYPE_LABELS,
  MATCH_TYPE_OPTIONS,
  planGroupPhase,
  SECOND_PHASE_LABELS,
  SECOND_PHASE_OPTIONS,
  SHORT_SET_LABELS,
  SHORT_SET_OPTIONS,
  SportIcon,
  systemOptionsFor,
  useClubCompetitionsQuery,
  useCompetitionQuery,
  useCreateCompetitionMutation,
  useCreateConsolationMutation,
  useParticipantsQuery,
  useUpdateCompetitionMutation,
  validateConsolationDraft,
  validateDraft,
  withCompetitionType,
  VISIBILITY_LABELS,
  VISIBILITY_OPTIONS,
  type CompetitionCategory,
  type CompetitionSystemCode,
  type CompetitionVisibilityCode,
  type ConsolationDraft,
  type Discipline,
  type DisciplineCode,
  type MatchTypeCode,
  type SecondPhaseTypeCode,
  type TournamentDraft,
} from '@/features/competition';
import { ApiError } from '@/shared/api/ApiError';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  DateInput,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  SegmentedControl,
  Select,
  Skeleton,
  Switch,
  Textarea,
  useToast,
} from '@/shared/ui';

/** Znak dyscypliny rysujemy tylko dla tych, dla ktorych mamy kolor w brandbooku. */
const ICON_DISCIPLINES: Record<string, Discipline> = {
  TENNIS: 'tennis',
  PADEL: 'padel',
  SQUASH: 'squash',
  PICKLEBALL: 'pickleball',
};

/** Pojedynczy wiersz panelu podsumowania: ikona, etykieta, wartosc. */
function SummaryRow({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 shrink-0 text-fg-subtle [&_svg]:size-4">{icon}</span>
      <div className="min-w-0">
        <dt className="text-xs text-fg-muted">{label}</dt>
        <dd className="text-sm font-semibold text-fg-strong">{value}</dd>
      </div>
    </div>
  );
}

/**
 * Ekran tworzenia rozgrywki — układ z makiety `tworzenie_turnieju.png`,
 * pola odwzorowujące `CreateCompetitionRequest`.
 */
export function CreateTournamentPage() {
  return <TournamentFormPage initialDraft={INITIAL_DRAFT} />;
}

/**
 * Ekran edycji rozgrywki — ten sam formularz co tworzenie, wypełniony
 * z istniejącej rozgrywki. Edycja jest możliwa do momentu losowania;
 * przy zapisanych graczach typ meczu i dyscyplina są zablokowane
 * (odpowiednik walidacji backendu).
 */
export function EditTournamentPage() {
  const { t } = useTranslation('tournament');
  const { competitionId = '' } = useParams<{ competitionId: string }>();
  const navigate = useNavigate();

  const competitionQuery = useCompetitionQuery(competitionId);
  const participantsQuery = useParticipantsQuery(competitionId);

  if (competitionQuery.isError) {
    return (
      <>
        <PageHeader title={t('edit.title')} />
        <Alert tone="danger">{t('edit.loadError')}</Alert>
      </>
    );
  }

  if (!competitionQuery.data || participantsQuery.isPending) {
    return (
      <div className="flex flex-col gap-5">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const competition = competitionQuery.data;
  // Losowanie startuje rozgrywkę — od statusu IN_PROGRESS backend odrzuca edycję.
  const editable =
    competition.status === 'DRAFT' ||
    competition.status === 'AWAITING_PAYMENT' ||
    competition.status === 'REGISTRATION_OPEN' ||
    competition.status === 'REGISTRATION_CLOSED';

  if (!editable) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader
          eyebrow={t('edit.eyebrow')}
          title={t('edit.title')}
          description={competition.name}
        />
        <Alert tone="info">
          <span className="flex flex-wrap items-center gap-3">
            {t('edit.locked')}
            <Button size="sm" variant="secondary" onClick={() => void navigate('/turnieje')}>
              {t('list.backToPanel')}
            </Button>
          </span>
        </Alert>
      </div>
    );
  }

  return (
    <TournamentFormPage
      // Odswiezenie danych po zapisie przebudowuje formularz od zera.
      key={`${competition.id}-${competition.updatedAt}`}
      initialDraft={draftFromCompetition(competition)}
      edit={{
        competitionId,
        competitionName: competition.name,
        hasParticipants: (participantsQuery.data ?? []).length > 0,
      }}
    />
  );
}

/** Kontekst trybu edycji — steruje nagłówkami, blokadami i zapisem. */
interface EditContext {
  competitionId: string;
  competitionName: string;
  /** Zapisani gracze blokują zmianę typu meczu i dyscypliny (jak w backendzie). */
  hasParticipants: boolean;
}

/**
 * Wspólny formularz rozgrywki dla tworzenia i edycji.
 *
 * Klub bierzemy z przełącznika w pasku górnym; formularz nie pyta o lokalizację,
 * bo rozgrywka zawsze powstaje w klubie, w którego kontekście pracuje użytkownik
 * (edycja lokalizacji nie zmienia).
 */
function TournamentFormPage({
  initialDraft,
  edit,
}: {
  initialDraft: TournamentDraft;
  edit?: EditContext;
}) {
  const { t } = useTranslation('tournament');
  const { t: tCommon } = useTranslation('common');
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { club, clubId, isEmpty: hasNoClubs } = useSelectedClub();

  const [draft, setDraft] = useState<TournamentDraft>(initialDraft);
  const createMutation = useCreateCompetitionMutation(clubId);
  const updateMutation = useUpdateCompetitionMutation(edit?.competitionId ?? '', clubId);

  const isEdit = edit !== undefined;

  // Tryb turnieju pocieszenia: zamiast konfiguracji — wybór turnieju zasadniczego,
  // z którego przegranych 1. rundy backend złoży drabinkę bez losowania.
  const [consolationOn, setConsolationOn] = useState(false);
  const [consolationDraft, setConsolationDraft] =
    useState<ConsolationDraft>(INITIAL_CONSOLATION_DRAFT);
  const consolationMutation = useCreateConsolationMutation(clubId);
  // Lista turniejów klubu potrzebna wyłącznie po włączeniu przełącznika.
  const consolationSourcesQuery = useClubCompetitionsQuery(
    !isEdit && consolationOn ? clubId : undefined,
  );
  const consolationSources = eligibleConsolationSources(consolationSourcesQuery.data ?? []);
  // Typ meczu i dyscyplina zamrożone przy zapisanych graczach — sloty (pary!)
  // i walidacja wyników zależą od obu, backend odrzuciłby zmianę z 409.
  const formatLocked = edit?.hasParticipants ?? false;

  // `null` znaczy, ze z podanych liczb nie da sie rozstawic grup.
  const groupPlan = hasAdvancementSettings(draft) ? planGroupPhase(draft) : null;
  // Podglad grup klasyfikacyjnych — sama liczba grup, bez kwalifikantow i drabinki.
  const classificationPlan =
    hasGroupPhase(draft) && !hasAdvancementSettings(draft) ? planClassificationPhase(draft) : null;

  /** Ustawia jedno pole formularza, zachowujac typ wartosci. */
  function update<TKey extends keyof TournamentDraft>(key: TKey, value: TournamentDraft[TKey]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  /** Ustawia jedno pole formularza pocieszenia. */
  function updateConsolation<TKey extends keyof ConsolationDraft>(
    key: TKey,
    value: ConsolationDraft[TKey],
  ) {
    setConsolationDraft((current) => ({ ...current, [key]: value }));
  }

  /**
   * Wybiera turniej zasadniczy i podpowiada nazwę pocieszenia, dopóki
   * organizator nie wpisał własnej (pole puste albo wciąż z podpowiedzią).
   */
  function selectConsolationSource(sourceId: string) {
    const source = consolationSources.find((competition) => competition.id === sourceId);
    const suggestion = source ? t('create.consolation.defaultName', { name: source.name }) : '';
    setConsolationDraft((current) => {
      const untouched =
        !current.name.trim() ||
        consolationSources.some(
          (competition) =>
            current.name === t('create.consolation.defaultName', { name: competition.name }),
        );
      return {
        ...current,
        sourceId,
        name: untouched && suggestion ? suggestion : current.name,
      };
    });
  }

  const dateFormatter = new Intl.DateTimeFormat('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  /** Formatuje zakres dat do postaci „24.05.2024 – 26.05.2024”. */
  function formatDateRange(): string {
    const parts = [draft.startDate, draft.endDate]
      .filter(Boolean)
      .map((value) => dateFormatter.format(new Date(value)));

    return parts.length > 0 ? parts.join(' – ') : t('create.summary.empty');
  }

  const onSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!club) return;

    if (!isEdit && consolationOn) {
      const consolationProblem = validateConsolationDraft(consolationDraft);
      if (consolationProblem) {
        showToast({ tone: 'warning', description: consolationProblem });
        return;
      }
      consolationMutation.mutate(
        {
          competitionId: consolationDraft.sourceId,
          request: buildCreateConsolationPayload(consolationDraft),
        },
        {
          onSuccess: () => {
            showToast({
              tone: 'success',
              description: t('create.consolation.created', {
                name: consolationDraft.name.trim(),
              }),
            });
            void navigate('/turnieje');
          },
          onError: (error) => {
            showToast({
              tone: 'danger',
              description:
                error instanceof ApiError ? error.message : t('create.consolation.failed'),
            });
          },
        },
      );
      return;
    }

    const problem = validateDraft(draft);
    if (problem) {
      showToast({ tone: 'warning', description: problem });
      return;
    }

    if (edit) {
      updateMutation.mutate(buildUpdateCompetitionPayload(draft), {
        onSuccess: () => {
          showToast({
            tone: 'success',
            description: t('edit.updated', { name: draft.name.trim() }),
          });
          void navigate('/turnieje');
        },
        onError: (error) => {
          showToast({
            tone: 'danger',
            description: error instanceof ApiError ? error.message : t('edit.failed'),
          });
        },
      });
      return;
    }

    createMutation.mutate(buildCreateCompetitionPayload(draft, club), {
      onSuccess: () => {
        showToast({
          tone: 'success',
          description: t('create.created', { name: draft.name.trim() }),
        });
        void navigate('/turnieje');
      },
      onError: (error) => {
        showToast({
          tone: 'danger',
          description: error instanceof ApiError ? error.message : t('create.failed'),
        });
      },
    });
  };

  if (hasNoClubs) {
    return (
      <>
        <PageHeader title={t('create.title')} description={t('create.description')} />
        <EmptyState
          icon={<Trophy />}
          title={t('create.noClubsTitle')}
          description={t('create.noClubsDescription')}
          className="min-h-[24rem]"
        />
      </>
    );
  }

  const iconDiscipline = ICON_DISCIPLINES[draft.discipline];

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-6">
      <PageHeader
        eyebrow={isEdit ? t('edit.eyebrow') : t('create.eyebrow')}
        title={isEdit ? t('edit.title') : t('create.title')}
        description={
          isEdit
            ? t('edit.description', { name: edit?.competitionName ?? '' })
            : t('create.description')
        }
      />

      {formatLocked && <Alert tone="info">{t('edit.formatLocked')}</Alert>}

      <div
        className={
          consolationOn
            ? 'flex flex-col gap-6'
            : 'grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]'
        }
      >
        {/* Kolumna lewa — formularz */}
        <div className="flex flex-col gap-6">
          {/* Przełącznik trybu pocieszenia — pierwszy element formularza tworzenia */}
          {!isEdit && (
            <Card padding="lg">
              <Switch
                label={t('create.consolation.switch')}
                description={t('create.consolation.switchHint')}
                checked={consolationOn}
                onChange={(event) => setConsolationOn(event.target.checked)}
              />
            </Card>
          )}

          {/* Tryb pocieszenia: zamiast konfiguracji tylko źródło, nazwa, widoczność i daty */}
          {consolationOn && (
            <Card padding="lg" className="flex flex-col gap-5">
              <CardHeader title={t('create.consolation.section')} className="mb-0" />

              {consolationSourcesQuery.isPending && <Skeleton className="h-24 w-full" />}
              {consolationSourcesQuery.isError && (
                <Alert tone="danger">{t('create.consolation.loadError')}</Alert>
              )}
              {consolationSourcesQuery.isSuccess && consolationSources.length === 0 && (
                <Alert tone="info">{t('create.consolation.noSources')}</Alert>
              )}

              {consolationSourcesQuery.isSuccess && consolationSources.length > 0 && (
                <>
                  <FormField label={t('create.consolation.source')} required>
                    <Select
                      selectSize="lg"
                      required
                      leadingIcon={<Trophy />}
                      placeholder={t('create.consolation.sourcePlaceholder')}
                      value={consolationDraft.sourceId}
                      onChange={(event) => selectConsolationSource(event.target.value)}
                    >
                      {consolationSources.map((competition) => (
                        <option key={competition.id} value={competition.id}>
                          {competition.name}
                        </option>
                      ))}
                    </Select>
                  </FormField>

                  <FormField label={t('create.fields.name')} required>
                    <Input
                      inputSize="lg"
                      required
                      value={consolationDraft.name}
                      onChange={(event) => updateConsolation('name', event.target.value)}
                    />
                  </FormField>

                  <div className="grid gap-5 sm:grid-cols-3">
                    <FormField label={t('create.fields.visibility')} required>
                      <Select
                        selectSize="lg"
                        required
                        leadingIcon={<Eye />}
                        value={consolationDraft.visibility}
                        onChange={(event) =>
                          updateConsolation(
                            'visibility',
                            event.target.value as CompetitionVisibilityCode,
                          )
                        }
                      >
                        {VISIBILITY_OPTIONS.map((visibility) => (
                          <option key={visibility} value={visibility}>
                            {VISIBILITY_LABELS[visibility]}
                          </option>
                        ))}
                      </Select>
                    </FormField>

                    <FormField label={t('create.fields.startDate')}>
                      <DateInput
                        inputSize="lg"
                        value={consolationDraft.startDate}
                        onChange={(event) => updateConsolation('startDate', event.target.value)}
                      />
                    </FormField>

                    <FormField label={t('create.fields.endDate')}>
                      <DateInput
                        inputSize="lg"
                        min={consolationDraft.startDate || undefined}
                        value={consolationDraft.endDate}
                        onChange={(event) => updateConsolation('endDate', event.target.value)}
                      />
                    </FormField>
                  </div>

                  <Switch
                    label={t('create.consolation.secondRound')}
                    description={t('create.consolation.secondRoundHint')}
                    checked={consolationDraft.includeSecondRound}
                    onChange={(event) =>
                      updateConsolation('includeSecondRound', event.target.checked)
                    }
                  />

                  <Alert tone="info">{t('create.consolation.hint')}</Alert>
                </>
              )}
            </Card>
          )}

          {!consolationOn && (
            <>
              <Card padding="lg" className="flex flex-col gap-5">
                <CardHeader title={t('create.sections.basics')} className="mb-0" />

                <FormField label={t('create.fields.name')} required>
                  <Input
                    inputSize="lg"
                    required
                    placeholder={t('create.fields.namePlaceholder')}
                    value={draft.name}
                    onChange={(event) => update('name', event.target.value)}
                  />
                </FormField>

                <FormField label={t('create.fields.description')}>
                  <Textarea
                    rows={2}
                    placeholder={t('create.fields.descriptionPlaceholder')}
                    value={draft.description}
                    onChange={(event) => update('description', event.target.value)}
                  />
                </FormField>

                <div className="grid gap-5 sm:grid-cols-2">
                  <FormField label={t('create.fields.discipline')} required>
                    <Select
                      selectSize="lg"
                      required
                      disabled={formatLocked}
                      {...(iconDiscipline
                        ? { leadingIcon: <SportIcon discipline={iconDiscipline} filled /> }
                        : {})}
                      value={draft.discipline}
                      onChange={(event) =>
                        update('discipline', event.target.value as DisciplineCode)
                      }
                    >
                      {DISCIPLINE_OPTIONS.map((discipline) => (
                        <option key={discipline} value={discipline}>
                          {DISCIPLINE_CODE_LABELS[discipline]}
                        </option>
                      ))}
                    </Select>
                  </FormField>

                  <FormField label={t('create.fields.visibility')} required>
                    <Select
                      selectSize="lg"
                      required
                      leadingIcon={<Eye />}
                      value={draft.visibility}
                      onChange={(event) =>
                        update('visibility', event.target.value as CompetitionVisibilityCode)
                      }
                    >
                      {VISIBILITY_OPTIONS.map((visibility) => (
                        <option key={visibility} value={visibility}>
                          {VISIBILITY_LABELS[visibility]}
                        </option>
                      ))}
                    </Select>
                  </FormField>

                  <FormField label={t('create.fields.startDate')}>
                    <DateInput
                      inputSize="lg"
                      value={draft.startDate}
                      onChange={(event) => update('startDate', event.target.value)}
                    />
                  </FormField>

                  <FormField label={t('create.fields.endDate')}>
                    <DateInput
                      inputSize="lg"
                      min={draft.startDate || undefined}
                      value={draft.endDate}
                      onChange={(event) => update('endDate', event.target.value)}
                    />
                  </FormField>
                </div>
              </Card>

              <Card padding="lg" className="flex flex-col gap-5">
                <CardHeader title={t('create.sections.format')} className="mb-0" />

                <div className="grid gap-5 lg:grid-cols-3">
                  {/* Bez `FormField`: grupa radio nie jest elementem etykietowalnym, wiec
                  `<label for>` wskazywalaby w prozne miejsce. */}
                  <div className="flex flex-col gap-1.5">
                    <span className="block text-[0.8125rem] font-medium text-fg-muted">
                      {t('create.fields.type')}
                    </span>
                    <SegmentedControl
                      label={t('create.fields.type')}
                      fullWidth
                      value={draft.type}
                      // Zmiana rodzaju moze uniewaznic wybrany system — poprawiamy go razem.
                      onValueChange={(value) =>
                        setDraft((current) => withCompetitionType(current, value))
                      }
                      options={COMPETITION_TYPE_OPTIONS.map((type) => ({
                        value: type,
                        label: COMPETITION_TYPE_LABELS[type] ?? type,
                      }))}
                      className="h-13"
                    />
                  </div>

                  <FormField label={t('create.fields.system')} required>
                    <Select
                      selectSize="lg"
                      required
                      value={draft.system}
                      onChange={(event) =>
                        update('system', event.target.value as CompetitionSystemCode)
                      }
                    >
                      {systemOptionsFor(draft.type).map((system) => (
                        <option key={system} value={system}>
                          {COMPETITION_SYSTEM_LABELS[system] ?? system}
                        </option>
                      ))}
                    </Select>
                  </FormField>

                  <FormField label={t('create.fields.matchType')} required>
                    <Select
                      selectSize="lg"
                      required
                      disabled={formatLocked}
                      value={draft.matchType}
                      onChange={(event) => update('matchType', event.target.value as MatchTypeCode)}
                    >
                      {MATCH_TYPE_OPTIONS.map((matchType) => (
                        <option key={matchType} value={matchType}>
                          {MATCH_TYPE_LABELS[matchType] ?? matchType}
                        </option>
                      ))}
                    </Select>
                  </FormField>

                  <FormField label={t('create.fields.category')}>
                    <Select
                      selectSize="lg"
                      value={draft.category}
                      onChange={(event) =>
                        update('category', event.target.value as CompetitionCategory)
                      }
                    >
                      {CATEGORY_OPTIONS.map((category) => (
                        <option key={category} value={category}>
                          {COMPETITION_CATEGORY_LABELS[category] ?? category}
                        </option>
                      ))}
                    </Select>
                  </FormField>

                  <FormField label={t('create.fields.maxPlayers')} required>
                    <Input
                      inputSize="lg"
                      required
                      type="number"
                      min={2}
                      value={draft.maxPlayers}
                      onChange={(event) => update('maxPlayers', event.target.value)}
                    />
                  </FormField>

                  <FormField
                    label={t('create.fields.matchDuration')}
                    hint={t('create.fields.matchDurationHint')}
                  >
                    <Input
                      inputSize="lg"
                      type="number"
                      min={5}
                      max={480}
                      step={5}
                      value={draft.estimatedMatchDurationMinutes}
                      onChange={(event) =>
                        update('estimatedMatchDurationMinutes', event.target.value)
                      }
                    />
                  </FormField>
                </div>

                {hasGroupPhase(draft) && (
                  <div className="flex flex-col gap-4 border-t border-border pt-5">
                    <div className="grid gap-5 lg:grid-cols-3">
                      <FormField label={t('create.fields.groupSize')}>
                        <Input
                          inputSize="lg"
                          type="number"
                          min={2}
                          value={draft.groupSize}
                          onChange={(event) => update('groupSize', event.target.value)}
                        />
                      </FormField>

                      {/* W grupach klasyfikacyjnych nikt nie odpada — pole awansu znika. */}
                      {hasAdvancementSettings(draft) && (
                        <FormField label={t('create.fields.advancingPerGroup')}>
                          <Input
                            inputSize="lg"
                            type="number"
                            min={1}
                            max={Number(draft.groupSize) - 1 || undefined}
                            value={draft.advancingPerGroup}
                            onChange={(event) => update('advancingPerGroup', event.target.value)}
                          />
                        </FormField>
                      )}

                      <FormField label={t('create.fields.secondPhaseType')}>
                        <Select
                          selectSize="lg"
                          value={draft.secondPhaseType}
                          onChange={(event) =>
                            update('secondPhaseType', event.target.value as SecondPhaseTypeCode)
                          }
                        >
                          {SECOND_PHASE_OPTIONS.map((phase) => (
                            <option key={phase} value={phase}>
                              {SECOND_PHASE_LABELS[phase]}
                            </option>
                          ))}
                        </Select>
                      </FormField>
                    </div>

                    {/* Awans z dalszych miejsc: najlepsi z kolejnego miejsca dopełniają
                    fazę finałową (maks. jeden na grupę). */}
                    {hasAdvancementSettings(draft) && (
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-8">
                        <Switch
                          label={t('create.fields.extraAdvancing')}
                          description={t('create.fields.extraAdvancingHint')}
                          checked={draft.extraAdvancing}
                          onChange={(event) => update('extraAdvancing', event.target.checked)}
                        />
                        {draft.extraAdvancing && (
                          <div className="sm:w-56">
                            <FormField label={t('create.fields.extraQualifiers')}>
                              <Input
                                inputSize="lg"
                                type="number"
                                min={1}
                                max={groupPlan?.groups ?? undefined}
                                value={draft.extraQualifiers}
                                onChange={(event) => update('extraQualifiers', event.target.value)}
                              />
                            </FormField>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Grupy klasyfikacyjne: bez kwalifikantow i drabinki — sama liczba grup. */}
                    {!hasAdvancementSettings(draft) &&
                      (classificationPlan ? (
                        <Alert tone="info">
                          {t('create.groupPlan.classification', {
                            maxPlayers: draft.maxPlayers,
                            groups: classificationPlan.groups,
                            size: draft.groupSize,
                          })}
                          <span className="mt-1 block opacity-80">
                            {t('create.groupPlan.estimate')}
                          </span>
                        </Alert>
                      ) : (
                        <Alert tone="warning">{t('create.groupPlan.invalid')}</Alert>
                      ))}

                    {/* Wielkosc drabinki finalowej nie jest wyborem — wynika z liczby grup
                    i liczby awansujacych, wiec pokazujemy ja razem z wyliczeniem. */}
                    {hasAdvancementSettings(draft) &&
                      (groupPlan ? (
                        <Alert tone={groupPlan.dropped > 0 ? 'warning' : 'info'}>
                          {groupPlan.extra > 0
                            ? t('create.groupPlan.summaryWithExtra', {
                                maxPlayers: draft.maxPlayers,
                                groups: groupPlan.groups,
                                advancing: draft.advancingPerGroup,
                                extra: groupPlan.extra,
                                place: Number(draft.advancingPerGroup) + 1,
                                qualifiers: groupPlan.qualifiers,
                                playoffSize: groupPlan.playoffSize,
                              })
                            : t('create.groupPlan.summary', {
                                maxPlayers: draft.maxPlayers,
                                groups: groupPlan.groups,
                                advancing: draft.advancingPerGroup,
                                qualifiers: groupPlan.qualifiers,
                                playoffSize: groupPlan.playoffSize,
                              })}
                          {groupPlan.dropped > 0 &&
                            ` ${t('create.groupPlan.dropped', { dropped: groupPlan.dropped })}`}
                          <span className="mt-1 block opacity-80">
                            {t('create.groupPlan.estimate')}
                          </span>
                        </Alert>
                      ) : (
                        <Alert tone="warning">{t('create.groupPlan.invalid')}</Alert>
                      ))}
                  </div>
                )}

                <div className="flex flex-col gap-4 border-t border-border pt-5 sm:flex-row sm:gap-8">
                  {hasBracketPhase(draft) && (
                    <Switch
                      label={t('create.fields.thirdPlaceMatch')}
                      description={t('create.fields.thirdPlaceMatchHint')}
                      checked={draft.thirdPlaceMatch}
                      onChange={(event) => update('thirdPlaceMatch', event.target.checked)}
                    />
                  )}

                  {hasPromotionRules(draft) && (
                    <>
                      <Switch
                        label={t('create.fields.promotions')}
                        description={t('create.fields.promotionsHint')}
                        checked={draft.promotions}
                        onChange={(event) => update('promotions', event.target.checked)}
                      />
                      <Switch
                        label={t('create.fields.relegations')}
                        description={t('create.fields.relegationsHint')}
                        checked={draft.relegations}
                        onChange={(event) => update('relegations', event.target.checked)}
                      />
                    </>
                  )}
                </div>
              </Card>

              <Card padding="lg" className="flex flex-col gap-5">
                <CardHeader title={t('create.sections.scoring')} className="mb-0" />

                <div className="grid gap-5 sm:grid-cols-2">
                  <FormField label={t('create.fields.bestOf')} required>
                    <Select
                      selectSize="lg"
                      required
                      value={draft.bestOf}
                      onChange={(event) => update('bestOf', event.target.value)}
                    >
                      {BEST_OF_OPTIONS.map((value) => (
                        <option key={value} value={value}>
                          {BEST_OF_LABELS[value]}
                        </option>
                      ))}
                    </Select>
                  </FormField>

                  <FormField label={t('create.fields.shortSetTarget')}>
                    <Select
                      selectSize="lg"
                      value={draft.shortSetTarget}
                      onChange={(event) => update('shortSetTarget', event.target.value)}
                    >
                      {SHORT_SET_OPTIONS.map((value) => (
                        <option key={value || 'standard'} value={value}>
                          {SHORT_SET_LABELS[value]}
                        </option>
                      ))}
                    </Select>
                  </FormField>
                </div>

                <div className="flex flex-col gap-4 sm:flex-row sm:gap-8">
                  {/* Przy jednym secie super tie-break zamieniłby cały mecz
                  w tie-break do 10 — wyłączamy przełącznik, payload i tak
                  wymusza false (`allowsSuperTiebreak`). */}
                  <Switch
                    label={t('create.fields.superTiebreak')}
                    description={
                      allowsSuperTiebreak(draft.bestOf)
                        ? t('create.fields.superTiebreakHint')
                        : t('create.fields.superTiebreakSingleSet')
                    }
                    checked={allowsSuperTiebreak(draft.bestOf) ? draft.superTiebreak : false}
                    disabled={!allowsSuperTiebreak(draft.bestOf)}
                    onChange={(event) => update('superTiebreak', event.target.checked)}
                  />
                  <Switch
                    label={t('create.fields.noAd')}
                    description={t('create.fields.noAdHint')}
                    checked={draft.noAd}
                    onChange={(event) => update('noAd', event.target.checked)}
                  />
                </div>
              </Card>
            </>
          )}
        </div>

        {/* Kolumna prawa — podsumowanie skladane na zywo z pol formularza */}
        {!consolationOn && (
          <div className="flex flex-col gap-4 xl:sticky xl:top-6">
            <Card padding="lg">
              <CardHeader title={t('create.summary.title')} />

              <dl className="flex flex-col gap-4">
                <SummaryRow
                  icon={<ClipboardList />}
                  label={t('create.summary.name')}
                  value={draft.name || t('create.summary.empty')}
                />
                <SummaryRow
                  icon={iconDiscipline ? <SportIcon discipline={iconDiscipline} /> : <Trophy />}
                  label={t('create.summary.discipline')}
                  value={DISCIPLINE_CODE_LABELS[draft.discipline]}
                />
                <SummaryRow
                  icon={<MapPin />}
                  label={t('create.summary.club')}
                  value={club?.name ?? t('create.summary.empty')}
                />
                <SummaryRow
                  icon={<CalendarDays />}
                  label={t('create.summary.dates')}
                  value={formatDateRange()}
                />
                <SummaryRow
                  icon={<Trophy />}
                  label={t('create.summary.format')}
                  value={`${COMPETITION_TYPE_LABELS[draft.type] ?? draft.type} • ${
                    COMPETITION_SYSTEM_LABELS[draft.system] ?? draft.system
                  }`}
                />
                <SummaryRow
                  icon={<Network />}
                  label={t('create.summary.matchType')}
                  value={MATCH_TYPE_LABELS[draft.matchType] ?? draft.matchType}
                />
                <SummaryRow
                  icon={<Users />}
                  label={t('create.summary.maxPlayers')}
                  value={draft.maxPlayers}
                />
                <SummaryRow
                  icon={<Award />}
                  label={t('create.summary.category')}
                  value={COMPETITION_CATEGORY_LABELS[draft.category] ?? draft.category}
                />
                <SummaryRow
                  icon={<Clock />}
                  label={t('create.summary.matchFormat')}
                  value={BEST_OF_LABELS[draft.bestOf]}
                />
                <SummaryRow
                  icon={<Clock />}
                  label={t('create.summary.matchDuration')}
                  value={t('create.summary.matchDurationValue', {
                    minutes: draft.estimatedMatchDurationMinutes || '60',
                  })}
                />
                {groupPlan && (
                  <SummaryRow
                    icon={<Grid3x3 />}
                    label={t('create.summary.groups')}
                    value={t('create.summary.groupsValue', {
                      groups: groupPlan.groups,
                      size: draft.groupSize,
                      playoffSize: groupPlan.playoffSize,
                    })}
                  />
                )}
                {classificationPlan && (
                  <SummaryRow
                    icon={<Grid3x3 />}
                    label={t('create.summary.groups')}
                    value={t('create.summary.classificationValue', {
                      groups: classificationPlan.groups,
                      size: draft.groupSize,
                    })}
                  />
                )}
                {hasBracketPhase(draft) && (
                  <SummaryRow
                    icon={<Medal />}
                    label={t('create.summary.thirdPlaceMatch')}
                    value={draft.thirdPlaceMatch ? t('create.summary.yes') : t('create.summary.no')}
                  />
                )}
              </dl>
            </Card>

            <Alert tone="info">{isEdit ? t('edit.hint') : t('create.draftHint')}</Alert>
          </div>
        )}
      </div>

      {/* Stopka akcji — pelna szerokosc obszaru tresci, jak w makiecie */}
      <div className="flex items-center justify-between gap-3 border-t border-border pt-5">
        <Button variant="secondary" size="lg" onClick={() => void navigate('/turnieje')}>
          {tCommon('actions.cancel')}
        </Button>

        <Button
          type="submit"
          size="lg"
          loading={
            createMutation.isPending || updateMutation.isPending || consolationMutation.isPending
          }
          disabled={!club}
          trailingIcon={<ArrowRight className="size-4" />}
        >
          {isEdit ? t('edit.submit') : t('create.submit')}
        </Button>
      </div>
    </form>
  );
}
